// Keep decoded grid previews alive between museum cycles, without retaining a
// whole collection or starting a burst of image requests at once.
export function createMuseumImageCache(onReady) {
  const ready = new Map(), active = new Map();
  let queue = [], disposed = false, bytes = 0;
  function pump() {
    while (!disposed && active.size < 3 && queue.length) load(queue.shift());
  }
  function load(url) {
    const image = new Image();
    let timer, done = false;
    const finish = success => {
      if (done) return;
      done = true; clearTimeout(timer);
      image.onload = image.onerror = null;
      active.delete(url);
      if (success && !disposed) {
        const size = image.naturalWidth * image.naturalHeight * 4;
        // Oversize originals may still use the browser's ordinary HTTP cache.
        if (size <= 128 * 1024 * 1024) {
          ready.set(url, { image, size }); bytes += size;
          while (ready.size > 24 || bytes > 128 * 1024 * 1024) {
            const oldest = ready.keys().next().value;
            bytes -= ready.get(oldest).size; ready.delete(oldest);
          }
          onReady();
        }
      }
      if (!success) image.removeAttribute('src');
      pump();
    };
    active.set(url, () => finish(false));
    image.onload = () => {
      if (image.decode) image.decode().then(() => finish(true), () => finish(false));
      else finish(image.naturalWidth > 0);
    };
    image.onerror = () => finish(false);
    timer = setTimeout(() => finish(false), 15000);
    image.src = url;
  }
  return {
    preload(urls) {
      if (disposed) return;
      queue = [...new Set(urls)].filter(url => !ready.has(url) && !active.has(url));
      pump();
    },
    get(url) {
      const entry = ready.get(url);
      if (!entry) return null;
      ready.delete(url); ready.set(url, entry);
      return entry.image;
    },
    dispose() {
      disposed = true; queue = [];
      [...active.values()].forEach(cancel => cancel());
      ready.clear(); bytes = 0;
    }
  };
}
